<template>
  <v-dialog v-model="show" max-width="500px" :fullscreen="isMobile">
    <div class="position-block-form full-height d-flex flex-column">
      <v-card tile class="overflow-y-auto d-flex flex-column">
        <div style="height: auto;" class="d-flex py-5 px-2 full-width overflow-hidden">
          <div class="toolbar-titles-container d-flex flex-column">
            <v-toolbar-title class="pl-5 text-overflow-ellipsis">{{ title }}</v-toolbar-title>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon v-if="isMobile" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="flex-grow-1 pt-0">
          <!-- positions -->
          <div class="mb-8">
            <!-- Kan bara välja en plats om redigerar en existerande platsavstängning -->
            <positions-picker
              v-model="pickerPositionIds"
              :show-select-all-positions-button="false"
              :show-unselect-all-positions-button="false"
              :select-multiple="!isExisitingPositionBlock"
            ></positions-picker>
          </div>

          <!-- closing date -->
          <div class="mb-6">
            <v-menu
              v-model="showClosingDatePickerMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="closingPickerDate"
                  label="Stängd från"
                  outlined
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  persistent-hint
                  hint="Första dagen då platsen inte går att boka"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="closingPickerDate"
                :first-day-of-week="1"
                @input="showClosingDatePickerMenu = false"
              ></v-date-picker>
            </v-menu>
          </div>

          <!-- opening date -->
          <div class="mb-6">
            <v-menu
              v-model="showOpeningDatePickerMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="openingPickerDate"
                  label="Öppnar igen"
                  outlined
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  persistent-hint
                  hint="Första dagen då platsen åter går att boka"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="openingPickerDate"
                :first-day-of-week="1"
                @input="showOpeningDatePickerMenu = false"
              ></v-date-picker>
            </v-menu>
          </div>

          <!-- reason -->
          <v-textarea
            v-model="reasonTextValue"
            outlined
            label="Anteckning"
            hint="Denna anteckning visas inte i bokningsportalen"
            counter="255"
            auto-grow
            rows="3"
          ></v-textarea>

        </v-card-text>

        <v-card-actions class="pa-4 mt-auto">
          <v-spacer></v-spacer>
          <v-btn color="success" @click="save" :loading="isSaving">Spara</v-btn>
        </v-card-actions>
      </v-card>
    </div>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex'

import PositionsPicker from '@/components/position/PositionsPicker.vue'

export default {
  name: 'PositionBlockForm',
  components: {
    PositionsPicker
  },
  data: () => ({
    showClosingDatePickerMenu: false,
    showOpeningDatePickerMenu: false
  }),
  computed: {
    ...mapState(
      {
        form: state => state.position.positionBlockForm
      }
    ),
    show: {
      get: function () {
        return this.form?.show ?? false
      },
      set: function (val) {
        this.$store.commit('position/SET_POSITION_BLOCK_FORM_SHOW', val)
      }
    },
    isSaving: function () {
      return this.form?.isSaving ?? false
    },
    isMobile: function () {
      return this.$vuetify.breakpoint.mdAndDown
    },
    pickerPositionIds: {
      get: function () {
        return this.form?.positionIds || []
      },
      set: function (val) {
        this.$store.commit('position/SET_POSITION_BLOCK_FORM_POSITION_IDS', val)
      }
    },
    reasonTextValue: {
      get: function () {
        return this.form?.reason || ''
      },
      set: function (val) {
        this.$store.commit('position/SET_POSITION_BLOCK_FORM_REASON', val)
      }
    },
    title: function () {
      if (this.isExisitingPositionBlock) {
        return 'Redigera platsavstängning'
      } else {
        return 'Ny platsavstängning'
      }
    },
    isExisitingPositionBlock: function () {
      return !!this.form?.positionBlockId
    },
    closingPickerDate: {
      get: function () {
        return this.form?.closingDate?.format('YYYY-MM-DD')
      },
      set: function (val) {
        const newClosingDate = window.dayjs.parseDate(val)
        this.$store.commit('position/SET_POSITION_BLOCK_FORM_CLOSING_DATE', newClosingDate)
        if (this.form?.openingDate && !this.form.openingDate.isAfter(newClosingDate)) {
          // Uppdaterar stängningsdatum om det inte är före nya öppningsdatumet
          const newOpeningDate = newClosingDate.add(1, 'days')
          this.$store.commit('position/SET_POSITION_BLOCK_FORM_OPENING_DATE', newOpeningDate)
        }
      }
    },
    openingPickerDate: {
      get: function () {
        return this.form?.openingDate?.format('YYYY-MM-DD')
      },
      set: function (val) {
        const newDate = window.dayjs.parseDate(val)
        this.$store.commit('position/SET_POSITION_BLOCK_FORM_OPENING_DATE', newDate)
        if (this.form?.closingDate && !this.form.closingDate.isBefore(newDate)) {
          // Uppdaterar stängningsdatum om det inte är före nya öppningsdatumet
          this.$store.commit('position/SET_POSITION_BLOCK_FORM_CLOSING_DATE', newDate.add(-1, 'days'))
        }
      }
    }
  },
  methods: {
    close: function () {
      this.show = false
    },
    save: function () {
      if (!this.form) { return }
      this.$store.dispatch('position/savePositionBlocks', {
        positionBlockId: this.form.positionBlockId || 0,
        positionIds: this.form.positionIds,
        closingDate: this.form.closingDate,
        openingDate: this.form.openingDate,
        reason: this.form.reason
      })
        .finally(() => {
          this.close()
        })
    }
  }
}
</script>
